"use client";

import type { DailyCall } from "@daily-co/daily-js";
import * as Monitoring from "~/lib/monitoring/client";

interface DailyTelemetryContext {
  sessionId?: string | null;
  therapyRelationshipId?: string;
  remoteParticipantCount?: number;
  attemptId?: string;
  pathname?: string | null;
  event?: string;
  diagnosticSummary?: Record<string, unknown>;
}

export interface DailyLifecycleEntry {
  name: string;
  event: string;
  at: string;
  offsetMs: number;
  payload: Record<string, unknown>;
  snapshot?: Record<string, unknown>;
}

const MAX_TIMELINE_ENTRIES = 20;

function toRecord(value: unknown): Record<string, unknown> {
  if (typeof value !== "object" || value === null) {
    return {};
  }

  return value as Record<string, unknown>;
}

export function getDailyErrorType(eventPayload: unknown): string | null {
  const errorType = toRecord(toRecord(eventPayload).error).type;

  return typeof errorType === "string" && errorType.length > 0
    ? errorType
    : null;
}

function getDailyErrorMessage(eventPayload: unknown): string | null {
  const eventRecord = toRecord(eventPayload);
  const errorMsg = eventRecord.errorMsg;
  if (typeof errorMsg === "string" && errorMsg.length > 0) {
    return errorMsg;
  }

  const nestedErrorMsg = toRecord(errorMsg).errorMsg;
  if (typeof nestedErrorMsg === "string" && nestedErrorMsg.length > 0) {
    return nestedErrorMsg;
  }

  const errorMessage = toRecord(eventRecord.error).msg;
  if (typeof errorMessage === "string" && errorMessage.length > 0) {
    return errorMessage;
  }

  return null;
}

function serializeValue(value: unknown): unknown {
  if (
    value === null ||
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    return value;
  }

  if (Array.isArray(value)) {
    return value.map(serializeValue);
  }

  if (value instanceof Error) {
    return {
      name: value.name,
      message: value.message,
    };
  }

  if (typeof value === "object") {
    const record = value as Record<string, unknown>;
    return Object.fromEntries(
      Object.entries(record).map(([key, entry]) => [
        key,
        serializeValue(entry),
      ]),
    );
  }

  return String(value);
}

function compactRecord(
  value: Record<string, unknown>,
): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(value).filter(([, entry]) => entry !== undefined),
  );
}

export function captureDailyDiagnostic(
  message: string,
  eventPayload: unknown,
  context: DailyTelemetryContext = {},
  level: "info" | "warning" | "error" = "warning",
) {
  const payload = serializeValue(eventPayload);

  Monitoring.addBreadcrumb({
    category: "daily.call",
    level,
    message,
    data: {
      ...context,
      payload,
    },
  });

  const tags = {
    feature_area: "daily_call",
    daily_event: context.event ?? "unknown",
  };
  const extra = {
    sessionId: context.sessionId,
    therapyRelationshipId: context.therapyRelationshipId,
    remoteParticipantCount: context.remoteParticipantCount,
    attemptId: context.attemptId,
    pathname: context.pathname,
    diagnosticSummary: context.diagnosticSummary,
    payload,
  };

  // Errors stay as Issues so they surface as grouped alerts. Info/warning
  // telemetry goes to PostHog technical monitoring events, which keeps error tracking focused on real bugs.
  if (level === "error") {
    Monitoring.captureMessage(message, { level, tags, extra });
    return;
  }

  const log =
    level === "warning" ? Monitoring.logger.warn : Monitoring.logger.info;
  log(message, { ...tags, ...extra });
}

export function buildDailyProviderSnapshot({
  roomUrl,
  userName,
}: {
  roomUrl: string;
  userName: string;
}): Record<string, unknown> {
  return {
    roomUrl,
    userName,
  };
}

export function getDailyEventType(eventPayload: unknown): string {
  const eventRecord = toRecord(eventPayload);
  const eventState = eventRecord.event;
  if (typeof eventState === "string" && eventState.length > 0) {
    return eventState;
  }

  const errorType = getDailyErrorType(eventPayload);
  if (errorType !== null) {
    return errorType;
  }

  const eventName = eventRecord.action;
  if (typeof eventName === "string" && eventName.length > 0) {
    return eventName;
  }

  const eventType = eventRecord.type;
  if (typeof eventType === "string" && eventType.length > 0) {
    return eventType;
  }

  return "unknown";
}

export function getDailyNetworkDiagnosticLevel(
  eventPayload: unknown,
): "info" | "warning" {
  const eventRecord = toRecord(eventPayload);
  const eventType = getDailyEventType(eventPayload);

  if (eventRecord.action !== "network-connection") {
    return eventType === "unknown" ? "info" : "warning";
  }

  return eventType === "disconnected" ? "warning" : "info";
}

export function getDailyCameraErrorDiagnosticLevel(
  eventPayload: unknown,
): "warning" | "error" {
  const errorType = getDailyErrorType(eventPayload);

  switch (errorType) {
    case "permissions":
    case "cam-in-use":
    case "mic-in-use":
    case "cam-mic-in-use":
    case "not-found":
    case "not-supported":
    case "undefined-mediadevices":
      return "warning";
    default:
      return "error";
  }
}

export function getDailyFatalErrorDiagnosticLevel(
  eventPayload: unknown,
): "warning" | "error" {
  const errorType = getDailyErrorType(eventPayload);

  switch (errorType) {
    case "meeting-full":
    case "nbf-room":
    case "nbf-token":
    case "not-allowed":
      return "warning";
  }

  const errorMessage = getDailyErrorMessage(eventPayload)?.toLowerCase() ?? "";
  if (
    errorMessage.includes("load failed") ||
    errorMessage.includes("failed to fetch") ||
    errorMessage.includes("networkerror") ||
    errorMessage.includes("network error")
  ) {
    return "warning";
  }

  return "error";
}

function safeDailyCallValue<T>(read: () => T): T | null {
  try {
    return read();
  } catch {
    return null;
  }
}

export function summarizeDailyPayload(
  eventPayload: unknown,
): Record<string, unknown> {
  const eventRecord = toRecord(eventPayload);
  const summary: Record<string, unknown> = {};

  for (const key of [
    "action",
    "event",
    "type",
    "callClientId",
    "errorMsg",
    "reason",
  ]) {
    const value = eventRecord[key];
    if (value !== undefined) {
      summary[key] = serializeValue(value);
    }
  }

  const error = toRecord(eventRecord.error);
  if (Object.keys(error).length > 0) {
    summary.error = serializeValue(
      compactRecord({
        type: error.type,
        msg: error.msg,
        blockedBy: error.blockedBy,
        blockedMedia: error.blockedMedia,
        missingMedia: error.missingMedia,
        reason: error.reason,
      }),
    );
  }

  const participant = toRecord(eventRecord.participant);
  if (Object.keys(participant).length > 0) {
    summary.participant = serializeValue(
      compactRecord({
        session_id: participant.session_id,
        user_id: participant.user_id,
        user_name: participant.user_name,
        local: participant.local,
        owner: participant.owner,
      }),
    );
  }

  if (eventRecord.participantCounts !== undefined) {
    summary.participantCounts = serializeValue(eventRecord.participantCounts);
  }

  if (eventRecord.meetingSession !== undefined) {
    summary.meetingSession = serializeValue(eventRecord.meetingSession);
  }

  if (eventRecord.meetingSessionState !== undefined) {
    summary.meetingSessionState = serializeValue(
      eventRecord.meetingSessionState,
    );
  }

  if (eventRecord.access !== undefined) {
    summary.access = serializeValue(eventRecord.access);
  }

  if (Object.keys(summary).length === 0) {
    return { payload: serializeValue(eventPayload) };
  }

  return summary;
}

export function buildDailyCallSnapshot(
  daily: DailyCall | null,
  extras: Record<string, unknown> = {},
): Record<string, unknown> {
  return {
    callClientId: daily?.callClientId ?? null,
    meetingState: daily ? safeDailyCallValue(() => daily.meetingState()) : null,
    accessState: daily
      ? serializeValue(safeDailyCallValue(() => daily.accessState()))
      : null,
    participantCounts: daily
      ? serializeValue(safeDailyCallValue(() => daily.participantCounts()))
      : null,
    localAudio: daily ? safeDailyCallValue(() => daily.localAudio()) : null,
    localVideo: daily ? safeDailyCallValue(() => daily.localVideo()) : null,
    isDestroyed: daily ? safeDailyCallValue(() => daily.isDestroyed()) : null,
    navigatorOnLine: typeof navigator !== "undefined" ? navigator.onLine : null,
    visibilityState:
      typeof document !== "undefined" ? document.visibilityState : null,
    pathname: typeof window !== "undefined" ? window.location.pathname : null,
    search: typeof window !== "undefined" ? window.location.search : null,
    ...extras,
  };
}

export function formatDailyLifecycleTimeline(
  timeline: DailyLifecycleEntry[],
): string[] {
  return timeline.map((entry) => {
    const details = compactRecord({
      event: entry.event,
      payload:
        Object.keys(entry.payload).length > 0 ? entry.payload : undefined,
      snapshot: entry.snapshot,
    });

    return `${entry.offsetMs}ms ${entry.name} ${JSON.stringify(details)}`;
  });
}

export function appendDailyLifecycleEntry(
  timeline: DailyLifecycleEntry[],
  entry: DailyLifecycleEntry,
): DailyLifecycleEntry[] {
  return [...timeline, entry].slice(-MAX_TIMELINE_ENTRIES);
}
